import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { ChartDataPoint, ForecastSettings } from "./types";

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  selectedYear: number;
  settings: ForecastSettings;
}

function CustomTooltip({ active, payload, label, selectedYear, settings }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload;
  const maleMentors = Math.round((data.ideal || 0) * (settings.malePercent / 100));
  const femaleMentors = (data.ideal || 0) - maleMentors;

  return (
    <div className="rounded-lg border bg-white p-4 shadow-lg">
      <p className="mb-2 font-semibold text-gray-900">{label} {selectedYear}</p>
      <div className="space-y-2 text-sm">
        <p className="text-gray-600">
          Forecasted Teens: <span className="font-medium text-gray-900">{data.forecastedTeens?.toLocaleString()}</span>
        </p>
        {data.actualTeens && (
          <p className="text-gray-600">
            Actual Teens: <span className="font-medium text-blue-600">{data.actualTeens?.toLocaleString()}</span>
          </p>
        )}
        <div className="border-t pt-2">
          <p className="font-medium text-red-600">
            Minimum: {data.minimum} mentors
          </p>
          {data.actual && (
            <p className="font-medium text-blue-600">
              Actual: {data.actual} mentors
            </p>
          )}
          <p className="font-medium text-green-600">
            Ideal: {data.ideal} mentors
          </p>
          <p className="mt-1 text-xs text-gray-500">
            ({maleMentors} male / {femaleMentors} female mentors)
          </p>
        </div>
        <div className="border-t pt-2 text-xs">
          <p>Lead Mentors: {data.minimumLeads} - {data.idealLeads}</p>
          <p>Supervisors: {data.minimumSupervisors} - {data.idealSupervisors}</p>
        </div>
      </div>
    </div>
  );
}

interface ForecastChartProps {
  chartData: ChartDataPoint[];
  selectedYear: number;
  settings: ForecastSettings;
}

export function ForecastChart({ chartData, selectedYear, settings }: ForecastChartProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Mentor Capacity Forecast</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
                label={{ value: "Mentors", angle: -90, position: "insideLeft", style: { fontSize: 12 } }}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    selectedYear={selectedYear}
                    settings={settings}
                  />
                }
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="minimum"
                name="Minimum (100% capacity)"
                stroke="#ef4444"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="actualPast"
                name="Actual"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="actualCurrent"
                name="Actual (Current)"
                stroke="#3b82f6"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls={false}
                legendType="none"
              />
              <Line
                type="monotone"
                dataKey="ideal"
                name={`Ideal (${100 - settings.bufferPercent}% capacity)`}
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
